import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { useNotificationStore } from "@/stores/notification-store";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

const failureNotices = new Map<string, string>();
const MAX_FAILURE_NOTICES = 100;
const failureToastId = (requestId: string) => `prompt-rejected-${requestId}`;

export function clearPromptFailure(requestId: string): void {
  failureNotices.delete(requestId);
  useNotificationStore.getState().removeSystemToast(failureToastId(requestId));
}

export function showPromptFailure(
  requestId: string,
  message: string,
  title = translate(libChatMessages, "requestDidNotStart"),
): void {
  const signature = JSON.stringify([title, message]);
  if (failureNotices.get(requestId) === signature) return;
  failureNotices.set(requestId, signature);
  if (failureNotices.size > MAX_FAILURE_NOTICES) {
    failureNotices.delete(failureNotices.keys().next().value!);
  }
  useNotificationStore.getState().addSystemToast({
    id: failureToastId(requestId),
    title,
    description: message,
    level: "error",
  });
}

function queuedToastId(requestId: string): string {
  return `prompt-queued-${requestId}`;
}

// The backend sends `delivery_pending: true` for every accepted prompt and
// follows up with `delivery_pending: false` once the companion confirms
// transport. That gap is usually milliseconds, so showing a toast
// immediately flickers on every request. Only show it when delivery stays
// pending long enough to mean the request is really queued.
export const PROMPT_QUEUED_TOAST_DELAY_MS = 2000;

const queuedToastTimers = new Map<string, ReturnType<typeof setTimeout>>();
const queuedToastMessages = new Map<string, string | undefined>();
const queuedToastRequestIds = new Set<string>();

function showPromptQueuedNow(requestId: string, message?: string): void {
  useNotificationStore.getState().addSystemToast({
    id: queuedToastId(requestId),
    title: translate(libChatMessages, "requestQueuedTitle"),
    description:
      message?.trim() || translate(libChatMessages, "requestQueuedDescription"),
    level: "info",
  });
}

export function showPromptQueued(requestId: string, message?: string): void {
  const toastId = queuedToastId(requestId);
  const existing = useNotificationStore
    .getState()
    .systemToasts.some((toast) => toast.id === toastId);
  if (existing) {
    if (message?.trim()) showPromptQueuedNow(requestId, message);
    return;
  }
  if (queuedToastTimers.has(requestId)) {
    if (message !== undefined) queuedToastMessages.set(requestId, message);
    return;
  }
  queuedToastRequestIds.add(requestId);
  queuedToastMessages.set(requestId, message);
  const isCurrentAuth = captureDesktopChatAuthScopeGuard();
  const timer = setTimeout(() => {
    queuedToastTimers.delete(requestId);
    const pendingMessage = queuedToastMessages.get(requestId);
    queuedToastMessages.delete(requestId);
    if (isCurrentAuth()) showPromptQueuedNow(requestId, pendingMessage);
  }, PROMPT_QUEUED_TOAST_DELAY_MS);
  queuedToastTimers.set(requestId, timer);
}

export function clearPromptQueued(requestId: string): void {
  const timer = queuedToastTimers.get(requestId);
  if (timer) {
    clearTimeout(timer);
    queuedToastTimers.delete(requestId);
  }
  queuedToastRequestIds.delete(requestId);
  queuedToastMessages.delete(requestId);
  useNotificationStore.getState().removeSystemToast(queuedToastId(requestId));
}

export function clearPromptNotices(): void {
  for (const requestId of [...queuedToastRequestIds])
    clearPromptQueued(requestId);
  for (const requestId of [...failureNotices.keys()])
    clearPromptFailure(requestId);
}
